import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "@/lib/toast";
import { X, Lock } from "@phosphor-icons/react";

/**
 * Reusable self-service change password modal.
 * Usage: <ChangePasswordDialog open={open} onClose={() => setOpen(false)} />
 */
export default function ChangePasswordDialog({ open, onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (next.length < 8) return setErr("New password must be at least 8 characters.");
    if (next !== confirm) return setErr("New password and confirmation do not match.");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      setCurrent(""); setNext(""); setConfirm("");
      onClose();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || "Failed to update password");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="change-password-dialog">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-md w-full p-8">
        <div className="flex justify-between items-start mb-6">
          <div>
            <div className="overline text-[#0A84FF] mb-1">Account Security</div>
            <h3 className="font-display font-black text-2xl text-black">Change password</h3>
          </div>
          <button type="button" onClick={onClose} className="text-neutral-500 hover:text-black" data-testid="change-password-close">
            <X size={22} />
          </button>
        </div>
        <div className="space-y-4">
          <Field label="Current Password" value={current} onChange={setCurrent} testId="cp-current" />
          <Field label="New Password" value={next} onChange={setNext} testId="cp-new" hint="Minimum 8 characters." />
          <Field label="Confirm New Password" value={confirm} onChange={setConfirm} testId="cp-confirm" />
          {err && <div className="text-sm text-red-600" data-testid="cp-error">{err}</div>}
          <button type="submit" disabled={busy} data-testid="cp-submit"
            className="w-full btn-mfg-primary px-4 py-3 rounded-md text-sm font-semibold disabled:opacity-60">
            {busy ? "Updating..." : "Update password"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, testId, hint }) {
  return (
    <div>
      <label className="overline text-neutral-500 block mb-2">{label}</label>
      <div className="relative">
        <Lock size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400" />
        <input
          type="password"
          required
          minLength={8}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          className="w-full bg-white border border-black/10 rounded-md pl-10 pr-4 py-3 text-black focus:outline-none focus:border-[#0A84FF] focus:ring-2 focus:ring-[#0A84FF]/20"
        />
      </div>
      {hint && <div className="text-xs text-neutral-500 mt-1">{hint}</div>}
    </div>
  );
}
